/**
 * "Tools & Calculators" section of the Metal Resources landing page (`/metal-resources/`): an
 * intro paragraph and one card per calculator, each linking to its page.
 *
 * Used by `src/components/MetalResources/MetalResourcesLandingRyerson.tsx` and
 * `MetalResourcesLandingSTS.tsx`, which pass the copy (`ToolsCalculatorsContent`) and add the
 * section to the landing page's anchor navigation. The standalone
 * `/metal-resources/tools-calculators/` page doesn't use this component; it renders
 * `components/References/SubPages/SubPages.tsx`.
 *
 * Desktop (`lg` and up) renders `./ToolsCalculatorsDesktop/ToolsCalculatorsDesktop.tsx` (title,
 * text and hover cards); mobile renders the intro, then one
 * `./ToolsCalculatorsMobile/ToolsCalculatorsMobile.tsx` card per entry. The mobile layout shows no
 * title.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { Media } from "@ryerson/frontend.next";
import { Typography } from "@ryerson/frontend.typography";
import ToolsCalculatorMobile from "./ToolsCalculatorsMobile/ToolsCalculatorsMobile";
import ToolsCalculatorsDesktop from "./ToolsCalculatorsDesktop/ToolsCalculatorsDesktop";
import styled from "@emotion/styled";

/**
 * One calculator card. `link` is the calculator page path; `linkText` is the mobile button label
 * (the desktop card is itself the link and shows no button). `imageUrl` is a path under this app's
 * `public/` folder.
 */
export type SecondaryContent = {
	title: string;
	description: string;
	imageUrl: string;
	link: string;
	linkText: string;
};

/**
 * Props of {@link ToolsCalculators}. `title` is shown on desktop only; `secondaryContent` is the
 * card list, in display order.
 */
export interface ToolsCalculatorsProps {
	title: string;
	mainDescription: string;
	secondaryContent: SecondaryContent[];
}

const Description = styled.div`
	margin: 24px 0px 32px 0px;
`;

/** Renders the section; see the file header for the desktop and mobile split. */
const ToolsCalculators: React.FC<ToolsCalculatorsProps> = (props) => {
	const { theme } = useTheme();

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ToolsCalculatorsDesktop {...props} />
			</Media>
			<Media lessThan="lg">
				<Description>
					<Typography variant="p" size="md">
						{props.mainDescription}
					</Typography>
				</Description>
				{props.secondaryContent.map((content: SecondaryContent, index: number) => (
					<ToolsCalculatorMobile
						key={index}
						id={index}
						textColor={theme.colors.primary.lightGray}
						backgroundColor={theme.colors.primary.secondaryBrand}
						content={content}
					/>
				))}
			</Media>
		</>
	);
};

export default ToolsCalculators;
